import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Building2, Globe, Users, Shield, Loader2, Star, Server, Monitor, ArrowRight, Save, Cloud, ArrowLeft, LayoutDashboard } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import API_URL from '../lib/apiUrl';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { useAutoSave } from '../hooks/useAutoSave';


// Star Rating Component
const StarRating = ({ rating, onRatingChange, disabled, isNo }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && onRatingChange(star)}
          onMouseEnter={() => !disabled && setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          className={`transition-all duration-150 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-110'}`}
        >
          <Star
            className={`h-8 w-8 ${
              isNo
                ? 'fill-red-500 text-red-500'
                : (hoverRating || rating) >= star
                  ? 'fill-green-500 text-green-500'
                  : 'fill-slate-200 text-slate-300'
            }`}
          />
        </button>
      ))}
      <span className="ml-3 text-sm text-slate-700 font-medium">
        {isNo ? 'Not Aware' : rating > 0 ? `${rating}/5 Awareness Level` : 'Select your awareness level'}
      </span>
    </div>
  );
};

const OrganizationForm = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [existingOrgId, setExistingOrgId] = useState(null);
  const [formData, setFormData] = useState({
    org_name: '',
    website: '',
    num_employees: '',
    industry: '',
    has_compliance_team: '',
    isms_awareness: '',
    isms_awareness_rating: 0,
    data_storage_location: '',
    data_storage_other_details: '',
    has_dedicated_it_team: ''
  });
  
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const orgIdFromUrl = searchParams.get('orgId');
  const isNewAssessment = searchParams.get('new') === 'true';
  const customerOrgId = searchParams.get('customerOrg');
  const prefilledName = searchParams.get('name');
  const prefilledIndustry = searchParams.get('industry');

  // Auto-save hook - saves every 60 seconds (disabled for new assessments until first save)
  const { 
    isSaving, 
    lastSaved, 
    saveDraft, 
    deleteDraft,
    draftLoaded,
    saveSuccess
  } = useAutoSave('organization', existingOrgId, formData, setFormData, token, 30000, !initialLoading, false);

  // Load existing organization data (skip if new assessment)
  useEffect(() => {
    const loadExistingData = async () => {
      // If this is a new assessment, skip loading previous data
      if (isNewAssessment) {
        const headers = { Authorization: `Bearer ${token}` };
        const draftResponse = await axios.get(`${API_URL}/api/drafts/organization`, { headers }).catch(() => ({ data: null }));
        const prefillData = prefilledName ? { org_name: decodeURIComponent(prefilledName), industry: prefilledIndustry ? decodeURIComponent(prefilledIndustry) : '' } : {};
        setFormData((current) => ({ ...current, ...prefillData, ...(draftResponse.data?.draft?.data || {}) }));
        setInitialLoading(false);
        return;
      }
      
      try {
        let orgData = null;
        
        if (orgIdFromUrl) {
          const response = await axios.get(
            `${API_URL}/api/organizations/${orgIdFromUrl}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          orgData = response.data;
          setExistingOrgId(orgIdFromUrl);
        } else {
          const response = await axios.get(
            `${API_URL}/api/organizations/latest`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data && response.data.org_id) {
            orgData = response.data;
            setExistingOrgId(response.data.org_id);
          }
        }
        
        if (orgData) {
          const resolvedOrgId = orgData.org_id;
          const draftResponse = await axios.get(`${API_URL}/api/drafts/organization`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { org_id: resolvedOrgId }
          }).catch(() => ({ data: null }));
          setFormData(prev => ({
            ...prev,
            org_name: orgData.org_name || '',
            website: orgData.website || '',
            num_employees: orgData.num_employees || '',
            industry: orgData.industry || '',
            has_compliance_team: orgData.has_compliance_team || '',
            isms_awareness: orgData.isms_awareness || '',
            isms_awareness_rating: orgData.isms_awareness_rating || 0,
            data_storage_location: orgData.data_storage_location || '',
            data_storage_other_details: orgData.data_storage_other_details || '',
            has_dedicated_it_team: orgData.has_dedicated_it_team || '',
            ...(draftResponse.data?.draft?.data || {})
          }));
        }
      } catch (error) {
        console.log('No existing organization data');
      } finally {
        setInitialLoading(false);
      }
    };
    
    loadExistingData();
  }, [token, orgIdFromUrl, isNewAssessment, prefilledName, prefilledIndustry]);

  // Track unsaved changes
  const updateFormData = useCallback((newData) => {
    setFormData(newData);
    setHasUnsavedChanges(true);
  }, []);

  // Browser tab close warning - always show on assessment pages
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const message = 'Data you entered is not saved. Are you sure you want to close?';
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.org_name.trim()) {
      toast.error('Please enter organization name');
      return;
    }
    if (!formData.website.trim()) {
      toast.error('Please enter website');
      return;
    }
    if (!formData.num_employees) {
      toast.error('Please select number of employees');
      return;
    }
    if (!formData.industry.trim()) {
      toast.error('Please enter industry');
      return;
    }
    if (!formData.has_compliance_team) {
      toast.error('Please answer if you have a compliance/security team');
      return;
    }
    if (!formData.isms_awareness) {
      toast.error('Please answer if you are aware of Information Security Management System');
      return;
    }
    if (formData.isms_awareness === 'yes' && formData.isms_awareness_rating === 0) {
      toast.error('Please rate your ISMS awareness level');
      return;
    }
    if (!formData.data_storage_location) {
      toast.error('Please select where you store customer data');
      return;
    }
    if (formData.data_storage_location === 'other' && !formData.data_storage_other_details.trim()) {
      toast.error('Please specify the other physical devices');
      return;
    }
    if (!formData.has_dedicated_it_team) {
      toast.error('Please answer if you have a dedicated IT team');
      return;
    }
    
    setLoading(true);

    try {
      let orgId = existingOrgId;
      
      if (existingOrgId) {
        await axios.put(
          `${API_URL}/api/organizations/${existingOrgId}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        const response = await axios.post(
          `${API_URL}/api/organizations`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        orgId = response.data.org_id;
        setExistingOrgId(orgId);
      }

      setHasUnsavedChanges(false);
      await deleteDraft(); // Clean up draft after successful save
      toast.success('Organization details saved!');
      navigate(`/core-assessment/${orgId}`);
    } catch (error) {
      toast.error('Failed to save organization details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-slate-900 mx-auto mb-4" />
          <p className="font-body text-lg text-slate-600">Loading organization data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 py-12">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Back to Dashboard Button */}
          <div className="mb-6">
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard')}
              className="gap-2"
            >
              <LayoutDashboard className="h-4 w-4" />
              Back to Dashboard
            </Button>
          </div>

          {/* Header */}
          <div className="mb-8">
              <h1 className="font-heading font-bold text-4xl text-black mb-3" data-testid="org-form-title">
                Organization Details
              </h1>
              <p className="font-body text-lg text-slate-700">
                Tell us about your organization to begin the ISO 27001:2022 maturity assessment
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-xs uppercase tracking-widest text-slate-600">Step 1 of 5</span>
                <span className="font-mono text-xs text-slate-600">20% Complete</span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-slate-900 w-1/5 progress-bar"></div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8 space-y-6">
              
              {/* Organization Basics Section Header */}
              <div className="mb-2">
                <h3 className="font-heading font-semibold text-xl text-black mb-1">Organization Basics</h3>
                <p className="font-body text-sm text-slate-600">Provide details about your organization</p>
              </div>

              {/* a) Organization Name */}
              <div>
                <Label className="font-body text-sm font-medium text-black mb-2 block">
                  a) Organization Name <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3 h-5 w-5 text-slate-500" />
                  <Input
                    type="text"
                    value={formData.org_name}
                    onChange={(e) => updateFormData({ ...formData, org_name: e.target.value })}
                    placeholder="Your Company Name"
                    className="pl-10 text-black"
                    required
                    data-testid="org-name-input"
                  />
                </div>
              </div>

              {/* b) Website */}
              <div>
                <Label className="font-body text-sm font-medium text-black mb-2 block">
                  b) Website <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-3 h-5 w-5 text-slate-500" />
                  <Input
                    type="url"
                    value={formData.website}
                    onChange={(e) => updateFormData({ ...formData, website: e.target.value })}
                    placeholder="https://www.yourcompany.com"
                    className="pl-10 text-black"
                    data-testid="website-input"
                  />
                </div>
              </div>

              {/* c) Number of Employees */}
              <div>
                <Label className="font-body text-sm font-medium text-black mb-2 block">
                  c) Number of Employees <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.num_employees}
                  onValueChange={(value) => updateFormData({ ...formData, num_employees: value })}
                  required
                >
                  <SelectTrigger data-testid="employees-select" className="text-black">
                    <Users className="h-4 w-4 mr-2 text-slate-500" />
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1-10</SelectItem>
                    <SelectItem value="11-50">11-50</SelectItem>
                    <SelectItem value="51-200">51-200</SelectItem>
                    <SelectItem value="201-500">201-500</SelectItem>
                    <SelectItem value="501-1000">501-1000</SelectItem>
                    <SelectItem value="1000+">1000+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* d) Industry */}
              <div>
                <Label className="font-body text-sm font-medium text-black mb-2 block">
                  d) Industry <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="text"
                  value={formData.industry}
                  onChange={(e) => updateFormData({ ...formData, industry: e.target.value })}
                  placeholder="e.g., Financial Services, Healthcare, Technology"
                  className="text-black"
                  required
                  data-testid="industry-input"
                />
              </div>

              {/* e) Compliance/Security Team */}
              <div>
                <Label className="font-body text-sm font-medium text-black mb-2 block">
                  e) Do you have a compliance/security team? <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.has_compliance_team}
                  onValueChange={(value) => updateFormData({ ...formData, has_compliance_team: value })}
                >
                  <SelectTrigger data-testid="compliance-team-select" className="text-black">
                    <Shield className="h-4 w-4 mr-2 text-slate-500" />
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* f) ISMS Awareness */}
              <div>
                <Label className="font-body text-sm font-medium text-black mb-2 block">
                  f) Are you aware of Information Security Management System (ISMS)? <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.isms_awareness}
                  onValueChange={(value) => {
                    updateFormData({ 
                      ...formData, 
                      isms_awareness: value,
                      isms_awareness_rating: value === 'no' ? 0 : formData.isms_awareness_rating
                    });
                  }}
                >
                  <SelectTrigger data-testid="isms-awareness-select" className="text-black">
                    <Shield className="h-4 w-4 mr-2 text-slate-500" />
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
                
                {formData.isms_awareness && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <Label className="font-body text-sm text-slate-700 mb-3 block">
                      {formData.isms_awareness === 'yes' 
                        ? 'Rate your ISMS awareness level:' 
                        : 'ISMS Awareness Level:'}
                    </Label>
                    <StarRating
                      rating={formData.isms_awareness === 'no' ? 5 : formData.isms_awareness_rating}
                      onRatingChange={(rating) => updateFormData({ ...formData, isms_awareness_rating: rating })}
                      disabled={formData.isms_awareness === 'no'}
                      isNo={formData.isms_awareness === 'no'}
                    />
                  </div>
                )}
              </div>

              {/* g) Data Storage Location */}
              <div>
                <Label className="font-body text-sm font-medium text-black mb-2 block">
                  g) Do you store customer data on cloud/on premise/other devices? <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.data_storage_location}
                  onValueChange={(value) => updateFormData({ 
                    ...formData, 
                    data_storage_location: value,
                    data_storage_other_details: value !== 'other' ? '' : formData.data_storage_other_details
                  })}
                >
                  <SelectTrigger data-testid="data-storage-select" className="text-black">
                    <Server className="h-4 w-4 mr-2 text-slate-500" />
                    <SelectValue placeholder="Select storage location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cloud">On Cloud</SelectItem>
                    <SelectItem value="on_premise">On Premise</SelectItem>
                    <SelectItem value="other">Other Physical Devices</SelectItem>
                  </SelectContent>
                </Select>
                
                {formData.data_storage_location === 'other' && (
                  <div className="mt-3">
                    <Textarea
                      value={formData.data_storage_other_details}
                      onChange={(e) => updateFormData({ ...formData, data_storage_other_details: e.target.value })}
                      placeholder="Please specify the physical devices (e.g., External hard drives, USB drives, Local servers, etc.)"
                      className="min-h-[80px] text-black"
                      data-testid="data-storage-other-input"
                    />
                  </div>
                )}
              </div>

              {/* h) Dedicated IT Team */}
              <div>
                <Label className="font-body text-sm font-medium text-black mb-2 block">
                  h) Do you have a dedicated IT team and systems? <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.has_dedicated_it_team}
                  onValueChange={(value) => updateFormData({ ...formData, has_dedicated_it_team: value })}
                >
                  <SelectTrigger data-testid="it-team-select" className="text-black">
                    <Monitor className="h-4 w-4 mr-2 text-slate-500" />
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Submit */}
              <div className="pt-6 border-t border-slate-200">
                {/* Save Draft Button with Status */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    {isSaving ? (
                      <span className="flex items-center gap-1 text-slate-500">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Saving...
                      </span>
                    ) : saveSuccess ? (
                      <span className="flex items-center gap-1 text-green-600 font-medium">
                        <Cloud className="h-4 w-4" />
                        Draft saved successfully
                      </span>
                    ) : lastSaved ? (
                      <span className="text-slate-500">
                        <Cloud className="h-4 w-4 inline mr-1" />
                        Last saved: {lastSaved.toLocaleTimeString()}
                      </span>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={saveDraft}
                    disabled={isSaving}
                    className="gap-2"
                  >
                    <Save className="h-4 w-4" />
                    Save Draft
                  </Button>
                </div>
                
                <Button
                  type="submit"
                  className="w-full py-6 text-base font-semibold"
                  disabled={loading}
                  data-testid="next-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
  );
};

export default OrganizationForm;
